import { getAuth } from "firebase/auth";
import { USERS_API_BASE_URL, DATA_API_BASE_URL } from "../config/api";

// Telcofy support view (Trello #557): a Telcofy staff member opens a customer
// organisation read-only. While it's on, every request to the Telcofy API carries
// X-View-As-Company, so the backend answers as that organisation (as a viewer)
// and refuses every write with 403 support_view_read_only.
//
// Kept per browser tab (sessionStorage), so it never outlives the tab and a
// second tab can stay on your own account. Entering or leaving reloads the app,
// which clears every cache built for the other identity.

export interface SupportView {
  id: string;
  name: string;
}

export interface StaffOrganisation {
  id: string;
  name: string | null;
  usersInUse: number;
  usersIncluded: number;
  viewable: boolean;
}

const KEY = "telcofy.supportView";
export const READ_ONLY_EVENT = "telcofy:read-only";

export function getSupportView(): SupportView | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SupportView) : null;
  } catch {
    return null;
  }
}

export function startSupportView(view: SupportView) {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(view));
  } catch {
    // Without storage the view can't survive the reload; nothing else to do.
  }
  window.location.assign("/");
}

export function stopSupportView() {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
  window.location.assign("/");
}

const isApiUrl = (url: string) => url.startsWith(USERS_API_BASE_URL) || url.startsWith(DATA_API_BASE_URL);

/**
 * Adds the support-view header to Telcofy API requests, and announces any
 * read-only refusal (support view, or a viewer) so the app can explain it
 * instead of showing a generic error. Install once, before the app renders.
 */
export function installSupportViewFetch() {
  const original = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    if (!isApiUrl(url)) return original(input, init);
    const view = getSupportView();
    let nextInit = init;
    // The staff endpoints are about the staff member themselves, never "as" a customer.
    if (view && !url.startsWith(`${USERS_API_BASE_URL}/staff/`)) {
      const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
      headers.set("X-View-As-Company", view.id);
      nextInit = { ...init, headers };
    }
    return original(input, nextInit).then((res) => {
      if (res.status === 403) {
        res
          .clone()
          .json()
          .then((body) => {
            if (body?.error === "support_view_read_only" || body?.error === "editor_role_required")
              window.dispatchEvent(new CustomEvent(READ_ONLY_EVENT, { detail: body.error }));
          })
          .catch(() => {});
      }
      return res;
    });
  };
}

/** Customer organisations a Telcofy staff member may open. Rejects for everyone else. */
export async function listStaffOrganisations(): Promise<StaffOrganisation[]> {
  const user = getAuth().currentUser;
  if (!user) throw new Error("not_signed_in");
  const res = await fetch(`${USERS_API_BASE_URL}/staff/organisations`, {
    headers: { Authorization: `Bearer ${await user.getIdToken()}` },
  });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return ((await res.json()).organisations ?? []) as StaffOrganisation[];
}
